"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { fetchLivescoreBoard } from "@/features/live-data/livescoreClient";
import type { LivescoreQuery } from "@/features/live-data/livescoreParams";
import type { LivescoreSport, LivescoreFilter } from "@/views/livescore/utils/livescore";
import { useLivescoreState } from "@/views/livescore/state/LivescoreStateContext";
import type { LocaleCode } from "@/lib/labels/types";
import { HydrationTracker } from "./hydrationTracker";
interface UseGroupHydrationOptions {
  sport: LivescoreSport;
  filter: LivescoreFilter;
  date: string;
  locale: LocaleCode;
}
export interface UseGroupHydrationResult {
  /** Resolve one competition's teams for the current scope and publish them to the shared entities. One attempt per scope. */
  hydrate: (competitionId: string) => void;
  /** True while that competition's request is in flight. */
  isLoading: (competitionId: string) => boolean;
  /** True once an attempt has finished, whatever it returned — rows then render with what they have. */
  isSettled: (competitionId: string) => boolean;
}

/**
 * Per-group team hydration for the collapsed tail of the livescore page. Opening a group asks
 * `GET /api/live/livescore` for that one competition at full hydration — one Search API chunk,
 * cached by the route for 15 s and by the data layer per entity after that — and merges the
 * entities into `useLivescoreState().entities`, where every row reads names and logos from.
 *
 * Each competition is attempted once per scope (see `HydrationTracker`); a new day, sport,
 * filter or locale starts over, and a response from an old scope is dropped. The callbacks keep
 * a stable identity so an effect depending on them does not re-run after every request.
 */
export function useGroupHydration({
  sport,
  filter,
  date,
  locale
}: UseGroupHydrationOptions): UseGroupHydrationResult {
  const {
    setEntities
  } = useLivescoreState();
  const trackerRef = useRef(new HydrationTracker());
  const [, bump] = useState(0);
  const scope = `${sport}|${filter}|${date}|${locale}`;
  const scopeRef = useRef(scope);
  const queryRef = useRef({
    sport,
    filter,
    date,
    locale
  });
  useEffect(() => {
    if (scopeRef.current !== scope) {
      scopeRef.current = scope;
      queryRef.current = {
        sport,
        filter,
        date,
        locale
      };
      trackerRef.current.reset();
      bump(v => v + 1);
    }
  }, [scope, sport, filter, date, locale]);
  const hydrate = useCallback((competitionId: string) => {
    const tracker = trackerRef.current;
    if (!tracker.start(competitionId)) return;
    bump(v_0 => v_0 + 1);
    const requestScope = scopeRef.current;
    const query: LivescoreQuery = {
      ...queryRef.current,
      competitions: [competitionId]
    };
    void fetchLivescoreBoard(query).then(board => {
      if (requestScope !== scopeRef.current) return; // the day/sport/filter changed meanwhile
      tracker.finish(competitionId);
      if (board) setEntities(board.entities);
      bump(v_1 => v_1 + 1);
    });
  }, [setEntities]);
  const isLoading = useCallback((competitionId_0: string) => trackerRef.current.isLoading(competitionId_0), []);
  const isSettled = useCallback((competitionId_1: string) => trackerRef.current.isSettled(competitionId_1), []);
  return {
    hydrate,
    isLoading,
    isSettled
  };
}